/**
 * A1 — Pestaña Facturación de la ficha del cliente: cifras, facturas, plan de pagos
 * por hitos, provisiones de fondos, igualas y tarifas. Todo lo económico de UN cliente
 * en un solo sitio.
 */
import { useEffect, useState, type ReactNode } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { AlertCircle, BookOpen, Hourglass, Loader2, Plus, Receipt, RotateCw } from 'lucide-react'
import { api } from '../../lib/api'
import { formatCents } from '../../lib/money'
import MilestonesPanel from '../billing/MilestonesPanel'
import PendingBillingList from '../billing/PendingBillingList'
import FundsProvisionPanel from '../billing/FundsProvisionPanel'
import ClientRatesPanel from '../rates/ClientRatesPanel'
import {
  InvoiceKindLabel,
  InvoiceStatusLabel,
  RecurringInvoicePeriodicityLabel,
  type ClientSummary,
  type InvoiceListResponse,
  type RecurringInvoiceListResponse,
} from '@toga/shared'

const th = 'px-5 py-2 text-left text-[11px] uppercase tracking-wider t-faint font-medium'
const td = 'px-5 py-2.5'
const fmtDate = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('es-ES') : '—')

function Figure({
  label,
  value,
  hint,
  tone,
}: {
  label: string
  value: string
  hint?: string
  tone?: 'warn' | 'good'
}) {
  return (
    <div className="surface p-4">
      <div className="text-[11px] uppercase tracking-wider t-faint">{label}</div>
      <div
        className={`text-xl font-semibold font-mono mt-1 ${
          tone === 'warn' ? 'text-amber-300' : tone === 'good' ? 'text-emerald-300' : 't-primary'
        }`}
      >
        {value}
      </div>
      {hint && <div className="text-[11px] t-faint mt-0.5">{hint}</div>}
    </div>
  )
}

function Section({
  title,
  icon,
  action,
  children,
}: {
  title: string
  icon: ReactNode
  action?: ReactNode
  children: ReactNode
}) {
  return (
    <section className="surface overflow-hidden">
      <header className="px-5 py-3.5 border-b border-base flex items-center justify-between gap-3">
        <h3 className="t-primary font-semibold text-sm flex items-center gap-2">
          {icon}
          {title}
        </h3>
        {action}
      </header>
      {children}
    </section>
  )
}

export default function ClientBillingTab({
  clientId,
  canWrite,
  summary,
}: {
  clientId: string
  canWrite: boolean
  summary: ClientSummary | null
}) {
  const navigate = useNavigate()
  const [invoices, setInvoices] = useState<InvoiceListResponse | null>(null)
  const [recurring, setRecurring] = useState<RecurringInvoiceListResponse | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    setError(null)
    Promise.all([
      api.get<InvoiceListResponse>(`/invoices?clientId=${clientId}&pageSize=100`),
      api.get<RecurringInvoiceListResponse>(`/recurring-invoices?clientId=${clientId}`),
    ])
      .then(([i, r]) => {
        if (!alive) return
        setInvoices(i)
        setRecurring(r)
      })
      .catch((err) => alive && setError(err instanceof Error ? err.message : 'Error al cargar'))
    return () => {
      alive = false
    }
  }, [clientId])

  const b = summary?.billing

  return (
    <div className="space-y-5">
      {error && (
        <div className="text-xs text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2 flex items-center gap-2">
          <AlertCircle size={13} /> {error}
        </div>
      )}

      {b && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Figure label="Facturado" value={formatCents(b.billedCents)} hint="Base imponible" />
          <Figure label="Cobrado" value={formatCents(b.paidCents)} tone="good" />
          <Figure
            label="Pendiente de cobro"
            value={formatCents(b.pendingCents)}
            tone={b.pendingCents > 0 ? 'warn' : undefined}
          />
          <Figure
            label="Provisión disponible"
            value={formatCents(summary?.funds.availableCents ?? 0)}
            hint="Saldo a favor del cliente"
          />
        </div>
      )}

      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
          <Figure
            label="Igualas activas"
            value={String(summary.recurring.active)}
            hint="Cuotas periódicas en marcha"
          />
          <Figure
            label="Hitos pendientes"
            value={String(summary.milestones.pendingCount)}
            hint={`${formatCents(summary.milestones.pendingCents)} de base`}
          />
          <Figure
            label="Tiempos sin facturar"
            value={`${(summary.unbilledTime.minutes / 60).toFixed(1).replace('.', ',')} h`}
            hint={formatCents(summary.unbilledTime.amountCents)}
            tone={summary.unbilledTime.amountCents > 0 ? 'warn' : undefined}
          />
        </div>
      )}

      <Section
        title="Pendientes de facturar"
        icon={<Hourglass size={15} className="text-toga-300" />}
        action={
          <Link to="/billing/pending" className="text-xs t-muted hover:underline">
            Ver todo el bufete
          </Link>
        }
      >
        <PendingBillingList clientId={clientId} hideClient />
      </Section>

      <Section
        title="Facturas"
        icon={<Receipt size={15} className="text-toga-300" />}
        action={
          <div className="flex items-center gap-2">
            <Link
              to={`/accounting/ledger?clientId=${clientId}`}
              title="Libro de facturas expedidas, IVA y cobros de este cliente"
              className="px-3 py-1.5 rounded-lg text-xs border border-base t-secondary hover:bg-hover hover:text-toga-200 flex items-center gap-1.5"
            >
              <BookOpen size={13} /> Libro del cliente
            </Link>
            {canWrite && (
              <button
                type="button"
                onClick={() => navigate(`/billing/new?clientId=${clientId}`)}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white bg-gradient-to-r from-toga-600 to-toga-700 hover:shadow-glow-toga flex items-center gap-1.5"
              >
                <Plus size={13} /> Nueva factura
              </button>
            )}
          </div>
        }
      >
        {!invoices ? (
          <div className="px-5 py-6 t-muted text-sm flex items-center gap-2">
            <Loader2 size={14} className="animate-spin" /> Cargando…
          </div>
        ) : invoices.items.length === 0 ? (
          <p className="px-5 py-8 text-sm t-faint text-center">Sin facturas para este cliente.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-base">
                  <th className={th}>Número</th>
                  <th className={th}>Tipo</th>
                  <th className={th}>Estado</th>
                  <th className={th}>Fecha</th>
                  <th className={th}>Expediente</th>
                  <th className={`${th} text-right`}>Total</th>
                  <th className={`${th} text-right`}>Cobrado</th>
                  <th className={th}>Gestoría</th>
                </tr>
              </thead>
              <tbody>
                {invoices.items.map((i) => (
                  <tr
                    key={i.id}
                    className="border-b border-base/50 last:border-0 hover:bg-hover/50"
                  >
                    <td className={`${td} font-mono text-xs whitespace-nowrap`}>
                      <Link to={`/billing/${i.id}`} className="text-toga-300 hover:underline">
                        {i.number ?? '(borrador)'}
                      </Link>
                    </td>
                    <td className={`${td} t-secondary text-xs`}>{InvoiceKindLabel[i.kind]}</td>
                    <td className={`${td} t-secondary text-xs`}>{InvoiceStatusLabel[i.status]}</td>
                    <td className={`${td} t-muted text-xs`}>{fmtDate(i.issueDate)}</td>
                    <td className={`${td} t-secondary text-xs font-mono`}>{i.caseCode ?? '—'}</td>
                    <td className={`${td} t-primary text-right font-mono`}>
                      {formatCents(i.totalCents)}
                    </td>
                    <td className={`${td} t-secondary text-right font-mono`}>
                      {formatCents(i.paidCents)}
                    </td>
                    <td className={`${td} text-xs whitespace-nowrap`}>
                      {i.status === 'BORRADOR' || i.kind === 'PROFORMA' ? (
                        <span className="t-faint">—</span>
                      ) : i.accountedAt ? (
                        <span className="text-emerald-300">
                          Pasada {new Date(i.accountedAt).toLocaleDateString('es-ES')}
                        </span>
                      ) : (
                        <span className="t-faint">Pendiente</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <MilestonesPanel clientId={clientId} canWrite={canWrite} />

      <FundsProvisionPanel clientId={clientId} readOnly={!canWrite} />

      <Section
        title="Igualas"
        icon={<RotateCw size={15} className="text-toga-300" />}
        action={
          <Link
            to="/settings/recurring"
            className="text-xs t-muted hover:text-toga-300 hover:underline"
          >
            Gestionar igualas →
          </Link>
        }
      >
        {!recurring ? (
          <div className="px-5 py-6 t-muted text-sm flex items-center gap-2">
            <Loader2 size={14} className="animate-spin" /> Cargando…
          </div>
        ) : recurring.items.length === 0 ? (
          <p className="px-5 py-8 text-sm t-faint text-center">
            Sin igualas. Una iguala factura una cuota fija periódica al cliente.
          </p>
        ) : (
          <ul className="divide-y divide-base/50">
            {recurring.items.map((r) => (
              <li key={r.id} className="px-5 py-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="t-primary text-sm truncate">{r.name}</div>
                  <div className="t-faint text-xs">
                    {RecurringInvoicePeriodicityLabel[r.periodicity]} · próxima emisión{' '}
                    {fmtDate(r.nextRunDate)}
                    {r.caseCode ? ` · ${r.caseCode}` : ''}
                  </div>
                </div>
                <span
                  className={`shrink-0 text-xs px-2 py-0.5 rounded border ${
                    r.status === 'ACTIVE'
                      ? 'border-emerald-500/30 text-emerald-300 bg-emerald-500/10'
                      : 'border-base t-faint'
                  }`}
                >
                  {r.status === 'ACTIVE' ? 'Activa' : r.status === 'PAUSED' ? 'Pausada' : r.status}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <ClientRatesPanel clientId={clientId} canWrite={canWrite} />
    </div>
  )
}
